<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    id: {
        type: String,
        default: undefined
    },
    // Classes da coluna em volta do campo.
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente se conecta
    // sozinho ao formulário pai (valor e erro), dispensando v-model e :erro.
    name: {
        type: String,
        default: null
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: String,
        default: ''
    },
    titulo: {
        type: String,
        default: 'Título'
    },
    // Dica exibida ao lado do título (ex.: onde a informação aparece).
    help: {
        type: String,
        default: null
    },
    placeholder: {
        type: String,
        default: ''
    },
    // Tipo do input (ex.: 'date').
    tipo: {
        type: String,
        default: 'text'
    },
    // Teclado sugerido no celular (ex.: 'numeric').
    inputmode: {
        type: String,
        default: undefined
    },
    maxlength: {
        type: [Number, String],
        default: undefined
    },
    disabled: {
        type: Boolean,
        default: false
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

// Getter para acompanhar mudanças no nome (ex.: índice de item em useFieldArray após remover outro).
const campoForm = props.name ? useField(() => props.name) : null;

const texto = computed({
    get: () => (campoForm ? campoForm.value.value : props.modelValue) ?? '',
    set: (value) => {
        if (campoForm) campoForm.value.value = value;
        emit('update:modelValue', value);
        emit('change', value);
    }
});

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraTexto() vs
// regraTexto({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div class="field mb-2" :class="props.classeColuna">
        <SmallTitle v-if="props.titulo" :titulo="props.titulo" :obrigatorio="obrigatorio" :help="props.help" />
        <InputText :id="props.id" v-model="texto" :type="props.tipo" :inputmode="props.inputmode" class="text-sm" :disabled="props.disabled"
            :placeholder="props.placeholder" :maxlength="props.maxlength" :class="{ 'p-invalid': mensagemErro }" />
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>
